import type { ReactElement } from 'react'
import { Menu as AriaMenu, Button, type Key, MenuItem, MenuTrigger, Popover } from 'react-aria-components'
import { cn } from '../lib/cn'

/** One menu entry. A disabled entry reports nothing and leaves the menu open. */
export interface MenuEntry {
	id: string
	label: string
	isDisabled?: boolean
}

/**
 * Props for the {@link Menu} atom.
 *
 * @property label - Trigger button text.
 * @property entries - The menu entries, in render order.
 * @property onAction - Reports the activated entry's `id`.
 * @property className - Extra Tailwind classes merged onto the trigger.
 *
 * @example
 * ```tsx
 * <Menu
 *   label="Actions"
 *   entries={[{ id: 'edit', label: 'Edit' }, { id: 'remove', label: 'Remove' }]}
 *   onAction={runAction}
 * />
 * ```
 */
export interface MenuProps {
	label: string
	entries: MenuEntry[]
	onAction?: (key: Key) => void
	className?: string
}

/**
 * Accessible menu primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** the entry list is absent from the accessibility tree
 *   until the trigger is activated; it then renders `role="menu"` with
 *   `role="menuitem"` entries, Arrow-key navigation and Escape to dismiss.
 * - **Selection is the consumer's:** the activated entry's `id` is reported
 *   through `onAction`. Only the transient open/closed state is React Aria's —
 *   it carries no business meaning.
 * - **Disabled entries** report nothing and leave the menu open.
 * - **Styling:** every colour, radius and spacing class is a `ds-*` token utility.
 *
 * @example
 * ```tsx
 * import { Menu } from '@today/ui-primitives'
 *
 * <Menu
 *   label="Actions"
 *   entries={[
 *     { id: 'edit', label: 'Edit' },
 *     { id: 'remove', label: 'Remove', isDisabled: !canRemove },
 *   ]}
 *   onAction={(key) => dispatch(key)}
 * />
 * ```
 *
 * @see packages/ui-primitives/src/menu/menu.stories.tsx for visual examples
 */
export function Menu({ label, entries, onAction, className }: MenuProps): ReactElement {
	return (
		<MenuTrigger>
			<Button
				className={cn(
					'h-10 rounded-ds-md border border-ds-border bg-ds-surface px-3 text-ds-base text-ds-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring',
					className,
				)}
			>
				{label}
			</Button>
			<Popover className="min-w-40 rounded-ds-md border border-ds-border bg-ds-surface shadow-lg">
				{/* Adapts React Aria's (key, ...rest) action signature down to the single
				    key this atom's contract reports. */}
				<AriaMenu onAction={(key) => onAction?.(key)} className="max-h-60 overflow-auto p-1 outline-none">
					{entries.map((entry) => (
						<MenuItem
							key={entry.id}
							id={entry.id}
							isDisabled={entry.isDisabled}
							className="cursor-pointer rounded-ds-sm px-2 py-1.5 text-ds-base text-ds-text outline-none data-[focused]:bg-ds-surface-muted data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
						>
							{entry.label}
						</MenuItem>
					))}
				</AriaMenu>
			</Popover>
		</MenuTrigger>
	)
}
